import React from "react";
import { PropTypes } from "prop-types";
//Material imports
import makeStyles from "@mui/styles/makeStyles";
import Button from "@mui/material/Button";
import FormGroup from "@mui/material/FormGroup";
import Typography from "@mui/material/Typography";
//Limitless imports
import { useTranslation } from "@limitless/common/hooks";
import { AutoComplete, createSuggestions, Checkbox } from "@limitless/portal";
import { ExpertTaskFilters, useExpertProfileContext } from "@limitless/expert";

const useStyles = makeStyles(theme => ({
    container: {
        width: "100%",
        backgroundColor: "#f4f4f4",
        position: "absolute",
        top: 67,
        borderTop: theme.palette.seperator
    },
    filterRoot: {
        flexDirection: "column",
        margin: "8px 16px 8px 16px",
        display: "flex"
    },
    filterOptionsContainer: {
        height: 56,
        backgroundColor: theme.background.paper,
        justifyContent: "space-between",
        padding: "0 16px 0 16px",
        borderRadius: 5,
        marginTop: 4,
        border: theme.palette.border
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "flex-end",
        marginTop: 16,
        marginRight: 16,
        marginBottom: 16
    },
    button: {
        marginLeft: 16
    },
    autoCompleteWrapper: {
        flexGrow: 1,
        position: "relative",
        border: "0px solid white",
        borderRadius: 5,
        backgroundColor: "white",
        marginTop: 4
    },
    checkboxLabel: {
        marginRight: 0
    }
}));

const TaskListFilters = ({ onDropDownFocus, closeFiltersFacet, filterComponentUpdate }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const {
        profile: { groups = [] }
    } = useExpertProfileContext();

    const filterComponent = React.useRef();

    React.useEffect(() => {
        filterComponentUpdate(filterComponent.current.clientHeight);
    }, [filterComponent?.current?.clientHeight]);

    const renderFilterButtons = (filterSet, onChange) => {
        return Object.keys(filterSet).map((filterKey, index) => {
            const filter = filterSet[filterKey];

            return (
                <Checkbox
                    key={index}
                    name={filterKey}
                    checked={filter.value}
                    label={t(filter.label)}
                    value={filterKey}
                    onChange={onChange}
                    checkboxLabelRoot={classes.checkboxLabel}
                />
            );
        });
    };

    const groupSuggestions = createSuggestions(groups, {
        labelDataKey: "displayName",
        valueDataKey: "groupName"
    });

    return (
        <ExpertTaskFilters>
            {({
                filters,
                isFilterSelected,
                onGroupChange,
                onTaskTypeFilterChange,
                onPassedTaskFilterChange,
                onClearFilters,
                onApplyFilters
            }) => {
                return (
                    <div className={classes.container} ref={filterComponent}>
                        <div className={classes.filterRoot}>
                            <Typography variant="body1" color="textPrimary">
                                {t("Task Type")}
                            </Typography>
                            <FormGroup row className={classes.filterOptionsContainer}>
                                {renderFilterButtons(filters.taskType, onTaskTypeFilterChange)}
                            </FormGroup>
                        </div>
                        <div className={classes.filterRoot}>
                            <Typography variant="body1" color="textPrimary">
                                {t("Passed Tasks")}
                            </Typography>
                            <FormGroup row className={classes.filterOptionsContainer}>
                                {renderFilterButtons(filters.passedTasks, onPassedTaskFilterChange)}
                            </FormGroup>
                        </div>
                        <div className={classes.filterRoot}>
                            <Typography variant="body1" color="textPrimary">
                                {t("Groups")}
                            </Typography>
                            <div className={classes.autoCompleteWrapper}>
                                <AutoComplete
                                    label=""
                                    value={filters.selectedGroups.value}
                                    placeholder="Type to search groups"
                                    id="autocompleteChipInputbox"
                                    suggestions={groupSuggestions}
                                    onChange={onGroupChange}
                                    name="groups"
                                    isMulti
                                    onFocus={onDropDownFocus}
                                    onBlur={onDropDownFocus}
                                />
                            </div>
                        </div>
                        <div className={classes.buttonContainer}>
                            <Button
                                id="clearFilterBtn"
                                color="secondary"
                                variant="outlined"
                                disabled={!isFilterSelected}
                                onClick={() => {
                                    onClearFilters();
                                    closeFiltersFacet();
                                }}
                            >
                                {t("Clear Filters")}
                            </Button>
                            <Button
                                id="applyFilterBtn"
                                variant="contained"
                                color="secondary"
                                className={classes.button}
                                disabled={!isFilterSelected}
                                onClick={() => {
                                    onApplyFilters();
                                    closeFiltersFacet();
                                }}
                            >
                                {t("Apply")}
                            </Button>
                        </div>
                    </div>
                );
            }}
        </ExpertTaskFilters>
    );
};

export default TaskListFilters;

TaskListFilters.propTypes = {
    filterComponentUpdate: PropTypes.func.isRequired,
    closeFiltersFacet: PropTypes.func,
    onDropDownFocus: PropTypes.func.isRequired
};
